import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { IProduct } from "../../../models/catalogue";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { SideNav } from "../SideNav";
import { defaults } from "../defaults";
import { GridFilterConfig } from "../filters";
import { GridVariant } from "../models";
import { updateSelectManyFilterValue, updateSortMethod } from "../reducers";
import { IReduxState } from "../reducers.interfaces";
import { listNonEmptyFilters } from "../selectors";
import { GridSorterConfig } from "../sorters";

import styles from "./index.module.scss";

const buildButtons = (
    filters: readonly GridFilterConfig[],
    products: ReadonlyArray<IProduct>,
    filterState: IReduxState["filters"],
    onButtonClick: (
        filterID: string,
        optionID: string,
        event: React.MouseEvent<HTMLButtonElement>,
    ) => void,
): React.JSX.Element[] => {
    const buttons: React.JSX.Element[] = [];

    for (const filterConfig of filters) {
        const options = filterConfig.listFilterOptions(products);
        const filterStateData = filterState[filterConfig.filterID];
        const selectedValues = new Set(
            filterStateData ? filterStateData.selectedValues : [],
        );
        for (const option of options) {
            if (selectedValues.has(option.id)) {
                buttons.push(
                    <button
                        key={`${filterConfig.filterID}-${option.id}`}
                        className={styles.filterTag}
                        onClick={(e) =>
                            onButtonClick(filterConfig.filterID, option.id, e)
                        }
                    >
                        <span className="ada-screenreader-only">
                            Remove filter
                        </span>
                        {option.label}
                    </button>,
                );
            }
        }
    }

    return buttons;
};

const buildFilters = (
    filters: readonly GridFilterConfig[],
    products: ReadonlyArray<IProduct>,
    filterState: IReduxState["filters"],
    onButtonClick: (
        filterID: string,
        optionID: string,
        event: React.MouseEvent<HTMLButtonElement>,
    ) => void,
    onButtonClear: (event: React.MouseEvent<HTMLButtonElement>) => void,
): React.JSX.Element | null => {
    const buttons = buildButtons(filters, products, filterState, onButtonClick);
    // No chips is not the same as an empty chip row: `.container` paints a
    // background and holds a 66px floor, so returning the wrapper anyway draws
    // a blank grey bar above the grid whenever nothing is filtered.
    if (buttons.length <= 0) {
        return null;
    }
    return (
        <div className={styles.tagContainer}>
            {buttons}
            {buttons.length > 1 && (
                <button className={styles.clearButton} onClick={onButtonClear}>
                    Clear All
                </button>
            )}
        </div>
    );
};

const buildFilterMenu = (
    gridVariant: GridVariant,
    filters: readonly GridFilterConfig[],
    isMobile: boolean,
): React.JSX.Element => {
    return (
        <div className={styles.filterContainer}>
            <SideNav
                gridVariant={gridVariant}
                filters={filters}
                isMobile={isMobile}
            />
        </div>
    );
};

const buildSortMenu = (
    sorters: readonly GridSorterConfig[] | undefined,
    hideSort: boolean | undefined,
    sortMethod: string | null,
    onChangeSortMethod: (sortMethod: string) => void,
): React.JSX.Element | null => {
    if (!sorters || hideSort) {
        return null;
    }

    return (
        <div className={styles.sortContainer}>
            <label htmlFor="sorter" className={styles.sortLabel}>
                {t`Sort by:`}
            </label>
            <select
                id="sorter"
                className={styles.sortSelect}
                value={sortMethod || sorters[0].id}
                onChange={(e) => {
                    onChangeSortMethod(e.currentTarget.value);
                }}
            >
                {sorters.map((sorter) => (
                    <option key={sorter.id} value={sorter.id}>
                        {sorter.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

export const TopNavComponent = (
    props: Immutable<{
        gridVariant: GridVariant;
        filters: readonly GridFilterConfig[];
        sorters?: readonly GridSorterConfig[];
        hideSort?: boolean;
        isMobile: boolean;
        products: ReadonlyArray<IProduct>;
        filterState: IReduxState["filters"];
        sortMethod: string | null;
    }> & {
        onButtonClick: (
            optionNamespace: string,
            filterID: string,
            optionID: string,
        ) => void;
        onChangeSortMethod: (sortMethod: string) => void;
    },
): React.ReactNode => {
    const handleButtonClick = (
        filterID: string,
        optionID: string,
        event: React.MouseEvent<HTMLButtonElement>,
    ): void => {
        event.preventDefault();
        const optionNamespace = props.products[0]?.product_class_slug || "";
        props.onButtonClick(optionNamespace, filterID, optionID);
    };

    const handleButtonClear = (
        event: React.MouseEvent<HTMLButtonElement>,
    ): void => {
        event.preventDefault();
        const optionNamespace = props.products[0]?.product_class_slug || "";
        for (const filterConfig of props.filters) {
            const options = filterConfig.listFilterOptions(props.products);
            const filterStateData = props.filterState[filterConfig.filterID];
            const selectedValues = new Set(
                filterStateData ? filterStateData.selectedValues : [],
            );
            for (const option of options) {
                if (selectedValues.has(option.id)) {
                    props.onButtonClick(
                        optionNamespace,
                        filterConfig.filterID,
                        option.id,
                    );
                }
            }
        }
    };

    const handleChangeSortMethod = (newSortMethod: string): void => {
        props.onChangeSortMethod(newSortMethod);
    };

    // TODO: Need figure out how to handle the default sort
    // this following code is commented out so that any sort method is not applied on page load.
    // useEffect(() => {
    //     if (props.sorters && !props.sortMethod) {
    //         handleChangeSortMethod(props.sorters[0].id);
    //     }
    // }, []);

    if (props.isMobile) {
        // `SideNav` narrows to the filters that have options to draw and
        // returns null when none do, so the wrapper around it has to ask the
        // same question -- otherwise the Filter affordance survives into a
        // state where opening it shows nothing. See `listNonEmptyFilters`.
        const hasFilterMenu =
            listNonEmptyFilters(
                props.filters.filter((config) => config.showInSidebar),
                props.products,
            ).length > 0;
        return (
            <div className={styles.container}>
                <div className={styles.mobileWrapper}>
                    {hasFilterMenu && (
                        <div className={styles.mobileFilterWrapper}>
                            {buildFilterMenu(
                                props.gridVariant,
                                props.filters,
                                props.isMobile,
                            )}
                        </div>
                    )}
                    <div className={styles.mobileSortWrapper}>
                        {buildSortMenu(
                            props.sorters,
                            props.hideSort,
                            props.sortMethod,
                            handleChangeSortMethod,
                        )}
                    </div>
                </div>
                {buildFilters(
                    props.filters,
                    props.products,
                    props.filterState,
                    handleButtonClick,
                    handleButtonClear,
                )}
            </div>
        );
    }

    const filterTags = buildFilters(
        props.filters,
        props.products,
        props.filterState,
        handleButtonClick,
        handleButtonClear,
    );
    const sortMenu = buildSortMenu(
        props.sorters,
        props.hideSort,
        props.sortMethod,
        handleChangeSortMethod,
    );
    // Both halves gone means there is nothing to put in the bar, and the bar
    // is not nothing: it paints a background, holds a 66px floor and adds
    // 1.75rem of margin either side. A grid whose sort moved to the sidebar
    // and whose chips are off hits this on every render, so drawing an empty
    // one would push the tiles down by a strip of grey that never fills.
    //
    // Desktop only. The mobile branch above returns before this, because its
    // wrapper carries the Filter and Sort controls -- the only way into the
    // sidebar on a small viewport -- and is never empty.
    if (filterTags === null && sortMenu === null) {
        return null;
    }

    return (
        <div className={styles.container}>
            {filterTags}
            {sortMenu}
        </div>
    );
};

export const TopNav = React.memo(
    (
        props: Immutable<{
            gridVariant: GridVariant;
            filters: readonly GridFilterConfig[];
            sorters?: readonly GridSorterConfig[];
            hideSort?: boolean;
            isMobile: boolean;
        }>,
    ): React.ReactNode => {
        const dispatch = useAppDispatch();
        const products = useAppSelector(
            (state) => state.productgrid2?.products || defaults.products,
        );
        const filterState = useAppSelector(
            (state) => state.productgrid2?.filters || defaults.filters,
        );
        const sortMethod = useAppSelector(
            (state) => state.productgrid2?.sortMethod || defaults.sortMethod,
        );

        const handleButtonClick = (
            optionNamespace: string,
            filterID: string,
            optionID: string,
        ): void => {
            dispatch(
                updateSelectManyFilterValue({
                    optionNamespace: optionNamespace,
                    filterID: filterID,
                    optionID: optionID,
                    isSelected: false,
                }),
            );
        };

        const handleChangeSortMethod = (newSortMethod: string): void => {
            dispatch(updateSortMethod(newSortMethod));
        };

        return (
            <TopNavComponent
                gridVariant={props.gridVariant}
                filters={props.filters}
                sorters={props.sorters}
                hideSort={props.hideSort}
                isMobile={props.isMobile}
                products={products}
                filterState={filterState}
                sortMethod={sortMethod}
                onButtonClick={handleButtonClick}
                onChangeSortMethod={handleChangeSortMethod}
            />
        );
    },
);
